import React, { useState } from 'react';
import ConfirmationPopup from '../Utils/ConfirmationPopup';
import UploadResume from './UploadResume';
import TalentStatus from '../TalentComponents/Utils/TalentStatus';
import CandidateStatus from '../CandidateComponents/Utils/CandidateStatus';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import { useSession } from '../Utils/SessionContext';
import CustomTooltip from '../Utils/CustomTooltip';
import { getColorForValue } from '../Utils/ColorUtils';

// Helper function to darken a hex color
const darkenColor = (hex, amount) => {
  // Remove the # if it exists
  hex = hex.replace('#', '');
  
  // Convert to RGB
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  
  // Darken each component
  r = Math.max(0, Math.floor(r * (1 - amount)));
  g = Math.max(0, Math.floor(g * (1 - amount)));
  b = Math.max(0, Math.floor(b * (1 - amount)));
  
  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const JobCandidates = ({ candidates, onViewCandidate, onAddCandidate, loading, error, onCandidateDeleted }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [popupTitle, setPopupTitle] = useState('Information');
  const [isConfirmDelete, setIsConfirmDelete] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);
  const [isUploadResumeOpen, setIsUploadResumeOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Get user session for authentication
  const { user } = useSession();
  
  const handleResendInvite = () => {
    setPopupTitle('Information');
    setPopupMessage('Coming soon');
    setIsConfirmDelete(false);
    setShowPopup(true);
  };
  
  const handleUploadForCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setIsUploadResumeOpen(true);
  };
  
  const handleDeleteCandidate = (candidate) => {
    setCandidateToDelete(candidate);
    setPopupTitle('Confirm Deletion');
    setPopupMessage(`Are you sure you want to delete ${candidate.talent?.fname} ${candidate.talent?.lname} from this job?`);
    setIsConfirmDelete(true);
    setShowPopup(true);
  };
  
  const confirmDeleteCandidate = async () => {
    if (!candidateToDelete) return;
    
    setIsDeleting(true);
    
    try {
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Build the URL with the candidate ID as a query parameter
      const url = new URL(API_ENDPOINTS.DELETE_CANDIDATE);
      url.searchParams.append('cid', candidateToDelete.candidate_id || candidateToDelete.id);
      
      // Make the request to delete the candidate
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      
      // Close the popup
      setShowPopup(false);
      
      // Call the callback to refresh the candidates list
      if (typeof onCandidateDeleted === 'function') {
        onCandidateDeleted();
      }
    } catch (error) {
      console.error('Error deleting candidate:', error);
      
      // Show error message
      setPopupTitle('Error');
      setPopupMessage(`Failed to delete candidate: ${error.message}`);
      setIsConfirmDelete(false);
      setShowPopup(true);
    } finally {
      setIsDeleting(false);
    }
  };

  // Check if candidates array has valid data
  const hasValidCandidates = candidates && candidates.length > 0;
  
  return (
    <>
      <div className="space-y-4">
        {/* Header section - always visible */}
        <div className="bg-gray-100 shadow-sm rounded-lg p-6 mb-6 border border-gray-200">
          <div className="flex justify-between items-start">
            <div className="mr-8">
              <h2 className="text-2xl font-bold text-gray-900">Candidate List</h2>
              <p className="text-gray-600 mt-2">
                This tab displays all candidates who have applied for this job position, along with their application status.
              </p>
            </div>
            <button
              onClick={onAddCandidate}
              className="px-4 py-2 bg-blue-500 text-white font-semibold rounded hover:bg-blue-600 transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              Add Candidate
            </button>
          </div>
        </div>
      
        {/* Table - always visible */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                  Candidate
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Candidate Status
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Candidate Job Fit
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center">
                    <p className="text-gray-600">Loading candidates...</p>
                  </td>
                </tr>
              )}
              
              {!loading && error && (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center">
                    <p className="text-red-500">{error}</p>
                    <p className="text-gray-600 mt-2">Please try again.</p>
                  </td>
                </tr>
              )}
              
              {!loading && !error && !hasValidCandidates && (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center">
                    <p className="text-sm text-gray-500 mb-4">No candidates found for this job position.</p>
                    <button
                      onClick={onAddCandidate}
                      className="px-4 py-2 bg-blue-500 text-white font-semibold rounded hover:bg-blue-600 transition-colors flex items-center mx-auto"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                      </svg>
                      Add Candidate
                    </button>
                  </td>
                </tr>
              )}
              
              {!loading && !error && hasValidCandidates && 
                candidates.map((candidate, index) => (
                  <tr 
                    key={index} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => onViewCandidate(candidate.candidate_id || candidate.id)}
                  >
                    <td className="px-6 py-5 whitespace-nowrap text-base text-gray-900">
                      <div>
                        <p className="font-bold">{candidate.talent?.fname} {candidate.talent?.lname}</p>
                        <p className="text-gray-600">{candidate.talent?.roleCategory?.subCategory || 'N/A'}</p>
                        <p className="text-xs text-gray-500">{candidate.candidate_id || candidate.id || 'N/A'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-base text-gray-900">
                      {candidate.created_at ? new Date(candidate.created_at).toLocaleString() : 'N/A'}
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-base text-gray-900">
                      <div className="space-y-2">
                        <CandidateStatus status={candidate.candidate_status} showDot={true} size="sm" />
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Talent:</span>
                          <TalentStatus status={candidate.talent?.status} showDot={true} size="sm" />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-center text-base">
                      <div className="flex space-x-3 justify-center">
                        <div 
                          className="border rounded-md p-2 w-24"
                          style={{ 
                            backgroundColor: candidate.job_profile_fit_report_json?.jobFit?.value !== undefined ? 
                              `${getColorForValue(candidate.job_profile_fit_report_json.jobFit.value)}20` : // 20 is hex for 12% opacity
                              '#f3f4f6',
                            borderColor: candidate.job_profile_fit_report_json?.jobFit?.value !== undefined ? 
                              getColorForValue(candidate.job_profile_fit_report_json.jobFit.value) : 
                              '#d1d5db'
                          }}
                        >
                          <p 
                            className="text-sm font-medium mb-1" 
                            style={{ 
                              color: candidate.job_profile_fit_report_json?.jobFit?.value !== undefined ? 
                                darkenColor(getColorForValue(candidate.job_profile_fit_report_json.jobFit.value), 0.3) : 
                                '#9ca3af' 
                            }}
                          >
                            Profile
                          </p>
                          <p 
                            className="text-xl font-bold"
                            style={{ 
                              color: candidate.job_profile_fit_report_json?.jobFit?.value !== undefined ? 
                                darkenColor(getColorForValue(candidate.job_profile_fit_report_json.jobFit.value), 0.1) : 
                                '#9ca3af' 
                            }}
                          >
                            {candidate.job_profile_fit_report_json?.jobFit?.value !== undefined ? 
                              `${Math.round(candidate.job_profile_fit_report_json.jobFit.value * 10)}%` : 
                              'N/A'}
                          </p>
                        </div>
                        <div 
                          className="border rounded-md p-2 w-24"
                          style={{ 
                            backgroundColor: candidate.job_evals_report_json?.jobFit?.value !== undefined ? 
                              `${getColorForValue(candidate.job_evals_report_json.jobFit.value)}20` : // 20 is hex for 12% opacity
                              '#f3f4f6',
                            borderColor: candidate.job_evals_report_json?.jobFit?.value !== undefined ? 
                              getColorForValue(candidate.job_evals_report_json.jobFit.value) : 
                              '#d1d5db'
                          }}
                        >
                          <p 
                            className="text-sm font-medium mb-1" 
                            style={{ 
                              color: candidate.job_evals_report_json?.jobFit?.value !== undefined ? 
                                darkenColor(getColorForValue(candidate.job_evals_report_json.jobFit.value), 0.3) : 
                                '#9ca3af' 
                            }}
                          >
                            Evaluation
                          </p>
                          <p 
                            className="text-xl font-bold"
                            style={{ 
                              color: candidate.job_evals_report_json?.jobFit?.value !== undefined ? 
                                darkenColor(getColorForValue(candidate.job_evals_report_json.jobFit.value), 0.1) : 
                                '#9ca3af' 
                            }}
                          >
                            {candidate.job_evals_report_json?.jobFit?.value !== undefined ? 
                              `${Math.round(candidate.job_evals_report_json.jobFit.value * 10)}%` : 
                              'N/A'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-right text-base font-medium">
                      <div className="flex justify-end space-x-3">
                        <CustomTooltip content="Upload Resume" position="left" minWidth="150px">
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              handleUploadForCandidate(candidate);
                            }}
                            className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
                            </svg>
                          </button>
                        </CustomTooltip>
                        
                        <CustomTooltip content="Delete" position="left" minWidth="80px">
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              handleDeleteCandidate(candidate);
                            }}
                            className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                            </svg>
                          </button>
                        </CustomTooltip>
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>
      
      
      {/* Popups */}
      <ConfirmationPopup
        show={showPopup}
        title={popupTitle}
        message={popupMessage}
        onConfirm={isConfirmDelete ? confirmDeleteCandidate : () => setShowPopup(false)}
        onCancel={() => setShowPopup(false)}
        confirmText={isConfirmDelete ? (isDeleting ? 'Deleting...' : 'Delete') : 'OK'}
        cancelText={isConfirmDelete ? 'Cancel' : null}
        isLoading={isDeleting}
      />
      
      <UploadResume
        isOpen={isUploadResumeOpen}
        onClose={() => {
          setIsUploadResumeOpen(false);
          setSelectedCandidate(null);
        }}
        onSubmit={() => {}} // Empty handler as per requirements
        candidateId={selectedCandidate?.candidate_id}
        talentId={selectedCandidate?.talent_id}
        // jobId={selectedCandidate?.job_id} // UNCLEAR WHY DO WE NEED THIS?????
        context="candidate"
      />
    </>
  );
};

export default JobCandidates;
